// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: a Teams meeting inside Konvo (proof of concept).
//
// Joins as the signed-in person's own Teams self through Microsoft's ACS calling SDK: the server
// swaps their Microsoft sign-in for an ACS token (POST /konvo/teams/meetings/token), the SDK joins
// the meeting link, and sound and video go straight between this device and Microsoft. The SDK
// (about 3 MB) is only loaded when a meeting starts. SDK objects are kept out of MobX; the store
// holds plain participant rows and bumps them when the SDK reports changes.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import KonvoCalendar from '@app/features/konvo_calendar/state/KonvoCalendar';
import KonvoUpNext from '@app/features/konvo_calendar/state/KonvoUpNext';
import TeamsInKonvo from '@app/features/konvo_teams_meetings/state/TeamsInKonvo';
import {isTeamsMeetingLink, teamsMeetingLocator} from '@app/features/konvo_teams_meetings/utils/TeamsMeetingLinks';
import {isNativeMobileShell} from '@app/features/platform/native_mobile/NativeMobileBridge';
import {http} from '@app/features/platform/transport/RestTransport';
import {HttpError} from '@app/features/platform/types/EndpointError';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {getElectronAPI, openExternalUrl} from '@app/features/ui/utils/NativeUtils';
import type {
	DeviceManager,
	LocalVideoStream,
	RemoteParticipant,
	RemoteVideoStream,
	TeamsCall,
	TeamsCallAgent,
} from '@azure/communication-calling';
import {makeAutoObservable, observable, runInAction} from 'mobx';

const logger = new Logger('TeamsMeetings');

export type TeamsMeetingStatus = 'idle' | 'joining' | 'lobby' | 'connected' | 'failed';
export type TeamsMeetingProblem = 'microsoft' | 'permissions' | 'join_failed';

export interface TeamsMeetingParticipant {
	key: string;
	name: string;
	muted: boolean;
	speaking: boolean;
	/** The camera stream, when the person's camera is on. */
	video: RemoteVideoStream | null;
	/** Their shared screen, when they share one. */
	screen: RemoteVideoStream | null;
}

interface TokenResponse {
	token: string;
	expires_on: string;
}

async function fetchToken(): Promise<string> {
	const response = await http.post<TokenResponse>(Endpoints.KONVO_TEAMS_MEETINGS_TOKEN, {body: {}});
	const token = response.body?.token;
	if (!token) throw Object.assign(new Error('no token'), {status: response.status});
	return token;
}

class TeamsMeetingsStore {
	/** Whether Join opens Teams meetings in Konvo for this person (null until the server answered). */
	enabled: boolean | null = null;
	status: TeamsMeetingStatus = 'idle';
	problem: TeamsMeetingProblem | null = null;
	title: string | null = null;
	meetingUrl: string | null = null;
	connectedAt: number | null = null;
	participants: Array<TeamsMeetingParticipant> = [];
	muted = false;
	cameraOn = false;
	sharing = false;
	/** This device's camera, for the self tile. */
	localVideo: LocalVideoStream | null = null;

	private agent: TeamsCallAgent | null = null;
	private devices: DeviceManager | null = null;
	private call: TeamsCall | null = null;
	private remotes = new Map<string, RemoteParticipant>();
	private configLoading: Promise<void> | null = null;

	constructor() {
		makeAutoObservable<TeamsMeetingsStore, 'agent' | 'devices' | 'call' | 'remotes' | 'configLoading'>(
			this,
			{
				agent: false,
				devices: false,
				call: false,
				remotes: false,
				configLoading: false,
				localVideo: observable.ref,
				participants: observable.ref,
			},
			{autoBind: true},
		);
	}

	/** A meeting is being joined or is running: updates must not reload the page now. */
	get active(): boolean {
		return this.status === 'joining' || this.status === 'lobby' || this.status === 'connected';
	}

	get screenShare(): TeamsMeetingParticipant | null {
		return this.participants.find((p) => p.screen) ?? null;
	}

	loadConfig(): Promise<void> {
		if (!this.configLoading) {
			this.configLoading = http
				.get<{enabled?: boolean}>(Endpoints.KONVO_TEAMS_MEETINGS_CONFIG)
				.then((response) => {
					runInAction(() => {
						this.enabled = response.body?.enabled === true;
					});
				})
				.catch(() => {
					runInAction(() => {
						this.enabled = false;
					});
				})
				.finally(() => {
					this.configLoading = null;
				});
		}
		return this.configLoading;
	}

	/**
	 * Join on a Teams meeting: inside Konvo when it is on for this person (desktop and web), the Teams
	 * app otherwise. Phones keep opening the Teams app until meetings run natively there.
	 */
	async open(
		url: string,
		title?: string | null,
		openOutside: (url: string) => unknown = openExternalUrl,
	): Promise<void> {
		// Desktop app: Teams' own web app inside Konvo's main area (TeamsInKonvo), or in a Konvo window
		// on older desktop apps. Free (the Microsoft 365 licence covers it), so it is on for everyone;
		// the meeting is then Teams', not this store's.
		if (isTeamsMeetingLink(url) && (await TeamsInKonvo.open(url, title ?? this.titleFor(url)))) return;
		const desktop = getElectronAPI();
		if (desktop?.teamsOpenMeeting && isTeamsMeetingLink(url)) {
			const opened = await desktop.teamsOpenMeeting(url, title ?? this.titleFor(url)).catch(() => false);
			if (opened) return;
		}
		if (this.enabled === null) await this.loadConfig();
		if (!this.enabled || isNativeMobileShell() || !isTeamsMeetingLink(url)) {
			void openOutside(url);
			return;
		}
		if (this.active && this.meetingUrl === url) return;
		if (this.active) await this.leave();
		await this.join(url, title ?? this.titleFor(url));
	}

	/** A meeting's title from the calendar, for links that arrive without one (chat cards). */
	private titleFor(url: string): string | null {
		const event = [...KonvoUpNext.events, ...KonvoCalendar.events].find((e) => e.teams_join_url === url);
		return event?.subject ?? null;
	}

	/** Leaves Konvo's meeting view and opens the same meeting in the Teams app. */
	async openInTeams(): Promise<void> {
		const url = this.meetingUrl;
		await this.leave();
		if (url) void openExternalUrl(url);
	}

	private async join(url: string, title: string | null): Promise<void> {
		this.reset();
		this.status = 'joining';
		this.meetingUrl = url;
		this.title = title;
		let token: string;
		try {
			token = await fetchToken();
		} catch (error) {
			logger.warn('Teams meeting token failed', error);
			// 409: Microsoft is not connected (reconnecting helps). Anything else is Konvo's side.
			const status = error instanceof HttpError ? error.status : (error as {status?: number}).status;
			runInAction(() => this.fail(status === 409 ? 'microsoft' : 'join_failed'));
			return;
		}
		try {
			const [{CallClient}, {AzureCommunicationTokenCredential}] = await Promise.all([
				import('@azure/communication-calling'),
				import('@azure/communication-common'),
			]);
			const credential = new AzureCommunicationTokenCredential({
				token,
				refreshProactively: true,
				tokenRefresher: fetchToken,
			});
			const client = new CallClient();
			const agent = await client.createTeamsCallAgent(credential);
			const devices = await client.getDeviceManager();
			const access = await devices.askDevicePermission({audio: true, video: false});
			if (!access.audio) {
				await agent.dispose();
				runInAction(() => this.fail('permissions'));
				return;
			}
			if (this.status !== 'joining' || this.meetingUrl !== url) {
				await agent.dispose();
				return;
			}
			this.agent = agent;
			this.devices = devices;
			const locator = teamsMeetingLocator(url);
			const options = {audioOptions: {muted: false}};
			const call = 'meetingLink' in locator ? agent.join(locator, options) : agent.join(locator, options);
			this.call = call;
			this.watchCall(call);
		} catch (error) {
			logger.error('Teams meeting join failed', error);
			await this.disposeAgent();
			runInAction(() => this.fail('join_failed'));
		}
	}

	private watchCall(call: TeamsCall): void {
		call.on('stateChanged', () => {
			runInAction(() => {
				if (this.call !== call) return;
				if (call.state === 'InLobby') this.status = 'lobby';
				else if (call.state === 'Connected') {
					this.status = 'connected';
					this.connectedAt ??= Date.now();
				} else if (call.state === 'Disconnected') {
					const reason = call.callEndReason;
					const neverIn = this.connectedAt === null;
					logger.info('Teams meeting ended', reason?.code, reason?.subCode);
					void this.disposeAgent();
					if (neverIn && reason && reason.code >= 400) this.fail('join_failed');
					else this.reset();
				}
			});
		});
		call.on('isMutedChanged', () => runInAction(() => (this.muted = call.isMuted)));
		call.on('isScreenSharingOnChanged', () => runInAction(() => (this.sharing = call.isScreenSharingOn)));
		call.on('remoteParticipantsUpdated', ({added, removed}) => {
			for (const participant of removed) this.remotes.delete(this.keyOf(participant));
			for (const participant of added) this.watchParticipant(participant);
			this.syncParticipants();
		});
		for (const participant of call.remoteParticipants) this.watchParticipant(participant);
		this.syncParticipants();
	}

	private keyOf(participant: RemoteParticipant): string {
		const id = participant.identifier as {rawId?: string; microsoftTeamsUserId?: string};
		return id.rawId ?? id.microsoftTeamsUserId ?? participant.displayName ?? 'unknown';
	}

	private watchParticipant(participant: RemoteParticipant): void {
		this.remotes.set(this.keyOf(participant), participant);
		const sync = () => this.syncParticipants();
		participant.on('isMutedChanged', sync);
		participant.on('isSpeakingChanged', sync);
		participant.on('displayNameChanged', sync);
		const watchStream = (stream: RemoteVideoStream) => stream.on('isAvailableChanged', sync);
		participant.videoStreams.forEach(watchStream);
		participant.on('videoStreamsUpdated', ({added}) => {
			added.forEach(watchStream);
			sync();
		});
	}

	private syncParticipants(): void {
		const rows = [...this.remotes.entries()].map(([key, participant]) => {
			const available = participant.videoStreams.filter((stream) => stream.isAvailable);
			return {
				key,
				name: participant.displayName ?? '',
				muted: participant.isMuted,
				speaking: participant.isSpeaking,
				video: available.find((stream) => stream.mediaStreamType === 'Video') ?? null,
				screen: available.find((stream) => stream.mediaStreamType === 'ScreenSharing') ?? null,
			};
		});
		runInAction(() => (this.participants = rows));
	}

	async toggleMute(): Promise<void> {
		const call = this.call;
		if (!call) return;
		await (call.isMuted ? call.unmute() : call.mute()).catch((error) => logger.warn('mute failed', error));
		runInAction(() => (this.muted = call.isMuted));
	}

	async toggleCamera(): Promise<void> {
		const call = this.call;
		const devices = this.devices;
		if (!call || !devices) return;
		try {
			if (this.localVideo) {
				await call.stopVideo(this.localVideo);
				runInAction(() => {
					this.localVideo = null;
					this.cameraOn = false;
				});
				return;
			}
			const access = await devices.askDevicePermission({audio: true, video: true});
			const camera = access.video ? (await devices.getCameras())[0] : undefined;
			if (!camera) return;
			const {LocalVideoStream} = await import('@azure/communication-calling');
			const stream = new LocalVideoStream(camera);
			await call.startVideo(stream);
			runInAction(() => {
				this.localVideo = stream;
				this.cameraOn = true;
			});
		} catch (error) {
			logger.warn('camera toggle failed', error);
		}
	}

	async toggleShare(): Promise<void> {
		const call = this.call;
		if (!call) return;
		try {
			if (call.isScreenSharingOn) await call.stopScreenSharing();
			else await call.startScreenSharing();
		} catch (error) {
			logger.warn('screen share toggle failed', error);
		}
		runInAction(() => (this.sharing = call.isScreenSharingOn));
	}

	async leave(): Promise<void> {
		const call = this.call;
		this.reset();
		if (call) await call.hangUp().catch(() => undefined);
		await this.disposeAgent();
	}

	/** Closes the failure card. */
	dismiss(): void {
		this.reset();
	}

	private fail(problem: TeamsMeetingProblem): void {
		this.status = 'failed';
		this.problem = problem;
		this.call = null;
		this.remotes.clear();
		this.participants = [];
	}

	private reset(): void {
		this.status = 'idle';
		this.problem = null;
		this.connectedAt = null;
		this.participants = [];
		this.muted = false;
		this.cameraOn = false;
		this.sharing = false;
		this.localVideo = null;
		this.call = null;
		this.remotes.clear();
	}

	private async disposeAgent(): Promise<void> {
		const agent = this.agent;
		this.agent = null;
		this.devices = null;
		if (agent) await agent.dispose().catch(() => undefined);
	}
}

export default new TeamsMeetingsStore();
